import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { ChartLineUp, CircleNotch, LinkSimple, Copy, Check } from "@phosphor-icons/react";
import { Header } from "@/components/Header";
import { API } from "@/lib/api";
import { TierBadge, fmtNum, fmtRate } from "@/components/TierBadge";

const field = "w-full border border-white/10 bg-[#0A0A0A] px-4 py-3 text-sm text-white placeholder-white/25 focus:border-amber focus:outline-none transition-colors duration-300";
const STATUS_STYLE = { approved: "text-emerald-300 border-emerald-400/40", pending: "text-amber border-amber/40", rejected: "text-crimson border-crimson/40" };

const Stat = ({ label, value, testid, accent = "text-[#EDEDED]" }) => (
  <div data-testid={testid} className="border border-white/10 bg-[#0A0A0A] p-5">
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
    <p className={`mt-2 font-serif text-3xl ${accent}`}>{value}</p>
  </div>
);

const TrackingLink = ({ handle, status }) => {
  const [copied, setCopied] = useState(false);
  // Share URL is served by the API so social crawlers get a personalized "Invited by" card, then redirects to /?ref=.
  const link = `${window.location.origin}/api/r/${handle}`;
  const directLink = `${window.location.origin}/?ref=${handle}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const t = document.createElement("textarea");
      t.value = link;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div data-testid="partner-tracking-link-box" className="mt-6 border-2 border-amber/70 bg-gradient-to-br from-amber/[0.12] to-amber/[0.02] p-5 sm:p-6 shadow-[0_0_40px_rgba(255,176,0,0.15)] rv-fade">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-amber">
        <LinkSimple size={16} weight="bold" /> Your Tracking Link
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-stretch">
        <code data-testid="partner-tracking-link" className="flex-1 break-all border border-white/15 bg-black px-4 py-3.5 font-mono text-sm sm:text-base text-white select-all">{link}</code>
        <button
          data-testid="partner-copy-tracking-link"
          onClick={copy}
          className={`flex items-center justify-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${copied ? "bg-emerald-400 text-black" : "rv-cta-glow bg-amber text-black"}`}
        >
          {copied ? <><Check size={16} weight="bold" /> Copied!</> : <><Copy size={16} weight="bold" /> Copy Link</>}
        </button>
      </div>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-white/45">
        {status === "approved" ? "Share it anywhere — shows your personal \"Invited by\" preview card, and every visitor is credited to you for 30 days." : "Your link starts earning credit once your application is approved."}
      </p>
      <p data-testid="partner-direct-link" className="mt-1.5 font-mono text-[10px] text-white/30 break-all">Direct link (also credited): {directLink}</p>
    </div>
  );
};

const TierProgress = ({ d }) => {
  if (!d.next_tier) {
    return <p data-testid="partner-tier-progress" className="mt-4 font-mono text-[11px] text-amber">Top tier reached — every visitor above {fmtNum(d.tiers[d.tiers.length - 1].min_uniques)} pays ${fmtRate(d.tier_rate)}/1k.</p>;
  }
  const from = d.tiers[d.tier_level - 1].min_uniques;
  const pct = Math.min(100, ((d.month_uniques - from) / (d.next_tier_at - from)) * 100);
  return (
    <div data-testid="partner-tier-progress" className="mt-4">
      <div className="flex justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-white/45">
        <span>{fmtNum(d.month_uniques)} visitors this month</span>
        <span>{fmtNum(d.next_tier_at - d.month_uniques)} to {d.next_tier}</span>
      </div>
      <div className="mt-2 h-1.5 bg-white/10"><div className="h-full bg-amber transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const Daily = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.uniques));
  return (
    <div data-testid="partner-daily-chart" className="mt-8 border border-white/10 bg-[#0A0A0A] p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">Unique visitors · last 30 days</p>
      {rows.length === 0 ? (
        <p className="font-mono text-[12px] text-white/35">No referred visits yet.</p>
      ) : (
        <div className="flex h-32 items-end gap-1.5">
          {rows.map((r) => (
            <div key={r.date} title={`${r.date}: ${r.uniques} uniques · ${r.pageviews} views`} className="flex-1 bg-amber/70 hover:bg-amber transition-colors" style={{ height: `${(r.uniques / max) * 100}%`, minHeight: 4 }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function PartnerStats() {
  const [q, setQ] = useState({ promoter_id: "", email: "" });
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data: d } = await axios.post(`${API}/partners/stats`, { ...q, promoter_id: q.promoter_id.trim().toLowerCase() });
      setData(d);
    } catch (e2) {
      setData(null);
      setErr(e2.response?.status === 404 ? "No partner found with that ID and email." : "Lookup failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet><title>Partner Stats · ReelVault</title></Helmet>
      <Header />
      <main className="mx-auto max-w-[1000px] px-5 sm:px-8 py-16 rv-fade">
        <h1 className="flex items-center gap-3 font-serif text-4xl sm:text-5xl tracking-tighter text-[#EDEDED]">
          <ChartLineUp size={40} weight="duotone" className="text-amber" /> Partner Stats
        </h1>
        <p className="mt-3 text-sm text-white/50">Enter the handle and email you applied with. Not a partner yet? <Link to="/partners" className="text-amber hover:underline">Apply here</Link>.</p>

        <form data-testid="partner-stats-form" onSubmit={lookup} className="mt-8 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <input data-testid="partner-stats-handle" required placeholder="your_handle" value={q.promoter_id} onChange={(e) => setQ({ ...q, promoter_id: e.target.value })} className={field} />
          <input data-testid="partner-stats-email" required type="email" placeholder="you@email.com" value={q.email} onChange={(e) => setQ({ ...q, email: e.target.value })} className={field} />
          <button data-testid="partner-stats-submit" type="submit" disabled={busy} className="flex items-center justify-center gap-2 bg-amber px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-black disabled:opacity-40">
            {busy && <CircleNotch size={14} className="animate-spin" />} View stats
          </button>
        </form>
        {err && <p data-testid="partner-stats-error" className="mt-4 font-mono text-[12px] text-crimson">{err}</p>}

        {data && (
          <section data-testid="partner-stats-result" className="mt-10">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="font-serif text-2xl text-[#EDEDED]">{data.name} <span className="text-white/35">@{data.promoter_id}</span></h2>
              <TierBadge testid="partner-stats-tier" name={data.tier} level={data.tier_level} />
              <span data-testid="partner-stats-status" className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE[data.status] || ""}`}>{data.status}</span>
            </div>
            <TrackingLink handle={data.promoter_id} status={data.status} />
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat testid="partner-stat-month-uniques" label="Uniques this month" value={fmtNum(data.month_uniques)} accent="text-amber" />
              <Stat testid="partner-stat-uniques" label="Lifetime uniques" value={fmtNum(data.uniques)} />
              <Stat testid="partner-stat-clicks" label="Affiliate clicks" value={fmtNum(data.affiliate_clicks)} />
              <Stat testid="partner-stat-payout" label="Est. payout this month" value={`$${data.estimated_payout.toFixed(2)}`} accent="text-emerald-300" />
            </div>
            <TierProgress d={data} />
            <Daily rows={data.daily || []} />
          </section>
        )}
      </main>
    </div>
  );
}
